import React from 'react';
import { makeAutoObservable } from 'mobx';
import ScoReflectClient from '../ScoReflectClient';
import { NavigateFunction } from 'react-router-dom';
import UserStore from '../UserStore';
import { AxiosError } from 'axios';

class AuthStore {
  private _stage: 'login' | 'otp' = 'login';
  private _otp: string = '';
  private _phoneNumber: string = '+7';
  private _token: string = '';
  private _isNotRobot: boolean = false;
  private _captchaKey: number = 0;
  private _captchaTimer: NodeJS.Timer = setTimeout(() => {});
  private _ttl: number = 0;
  private _ttlTimer: NodeJS.Timer = setTimeout(() => {});

  public get stage(): string {
    return this._stage;
  }

  public get phoneNumber(): string {
    return this._phoneNumber;
  }

  public get token(): string {
    return this._token;
  }

  public get captchaKey(): number {
    return this._captchaKey;
  }

  public get ttl(): number {
    return this._ttl;
  }

  constructor() {
    makeAutoObservable(this);
  }

  public onChangeOTP = (e: React.ChangeEvent<HTMLInputElement>) => {
    const otpErrorDiv = document.getElementById('otp_error') as HTMLDivElement;
    otpErrorDiv.style.display = 'none';
    const matrix = '___-___';
    var i = 0,
        def = matrix.replace(/\D/g, ""),
        val = e.target.value.replace(/\D/g, "");

    if (def.length >= val.length) val = def;

    e.target.value = matrix.replace(/./g, a => {
      return /[_\d]/.test(a) && i < val.length ? val.charAt(i++) : i >= val.length ? "" : a;
    });

    if (e.type === 'blur') {
      if (e.target.value.length === 2) e.target.value = '';
    } else {
      e.target.focus();
      if (e.target.setSelectionRange)
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
    }

    if (!/\d{3}-\d{3}/.test(e.target.value))
      otpErrorDiv.style.display = 'block';
    else
    otpErrorDiv.style.display = 'none';

    this._otp = e.target.value;
  }

  public onClickLogin = async (navigate: NavigateFunction) => {
    try {
      const response = await ScoReflectClient.request('/auth/login', {
        'PhoneNumber': this._phoneNumber.replaceAll(' ', ''),
        'Code': this._otp.replaceAll('-', '')
      });

      if (response.status === 200) {
        if (response.data['Success']) {
          UserStore.getUserData();
          navigate('/');
        }
      }
    } catch (error) {
      console.log(error);
      if (error instanceof AxiosError) {
        if (error.response) {
          if (error.status === 400) {
            const otpErrorDiv = document.getElementById('otp_error') as HTMLDivElement;
            for (let i = 0; i < error.response.data['Errors'].length; i++) {
              switch (error.response.data['Errors'][i]['Description']) {
                case 'Confirmation code not responsed':
                  otpErrorDiv.style.display = 'block';
                  break;
                case 'Invalid confirmation code':
                  otpErrorDiv.style.display = 'block';
                  break;
              }
            }
          }
        }
      }
      console.log(error);
    }
  }

  public onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumberErrorDiv = document.getElementById('phone_number_error') as HTMLDivElement;
    phoneNumberErrorDiv.style.display = 'none';
    const matrix = '+7 ___ ___ __ __';
    var i = 0,
        def = matrix.replace(/\D/g, ""),
        val = e.target.value.replace(/\D/g, "");

    if (def.length >= val.length) val = def;

    e.target.value = matrix.replace(/./g, a => {
      return /[_\d]/.test(a) && i < val.length ? val.charAt(i++) : i >= val.length ? "" : a;
    });

    if (e.type === 'blur') {
      if (e.target.value.length === 2) e.target.value = '';
    } else {
      e.target.focus();
      if (e.target.setSelectionRange)
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
    }

    if (!/\+7\s\d{3}\s\d{3}\s\d{2}\s\d{2}/.test(e.target.value))
      phoneNumberErrorDiv.style.display = 'block';
    else
      phoneNumberErrorDiv.style.display = 'none';

    this._phoneNumber = e.target.value;
  }

  public onSuccessCaptcha = async (token: string) => {
    const captchaErrorDiv = document.getElementById('captcha_error') as HTMLDivElement;
    captchaErrorDiv.style.display = 'none';

    this._token = token;
    clearTimeout(this._captchaTimer);

    try {
      const response = await ScoReflectClient.request('/auth/captcha', {
        'Token': this._token
      });

      if (response.status === 200)
        if (response.data['Success']) {
        this._captchaTimer = setTimeout(() => {
          this._token = '';
          this._captchaKey++;
        }, 300000);

        this._isNotRobot = true;
        return;
      }

      captchaErrorDiv.style.display = 'block';
      this._isNotRobot = false;
      this._captchaKey++;
    } catch (e) {
      captchaErrorDiv.style.display = 'block';
      this._isNotRobot = false;
      this._captchaKey++;
    }
  }

  public validate = (): boolean => {
    const isValidPhone = /\+7\s\d{3}\s\d{3}\s\d{2}\s\d{2}/.test(this._phoneNumber);

    return isValidPhone;
  }

  public onClickGoBack = (_: React.MouseEvent<SVGSVGElement>) => {
    this._stage = 'login';
  }

  public onClickContinue = async (_: React.MouseEvent<HTMLButtonElement>) => {
    if (this._ttl !== 0) {
      this._stage = 'otp';
      return;
    }

    try {
      const response = await ScoReflectClient.request('/auth/code', {
        'PhoneNumber': this._phoneNumber.replaceAll(' ', '')
      });

      if (response.status === 200) {
        this._ttl = response.data['TTL'];
        this._ttlTimer = setInterval(() => {
          if (this._ttl === 0) clearInterval(this._ttlTimer);
          else this._ttl--;
        }, 1000);
        this._stage = 'otp';
      }
    } catch (error) {
      console.log(error);
    }
  }

  public refresh = async () => {
    try {
      const response = await ScoReflectClient.request('/auth/refresh');

      if (response.status === 200) 
        if (response.data['Success'])
          UserStore.getUserData();
    } catch (error) {
      console.log(error);
    }
  }
}

// eslint-disable-next-line
export default new AuthStore();